import { Link, NavLink, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';

import {
  AttendanceIcon,
  BrandMarkIcon,
  ClientsIcon,
  CalendarIcon,
  DashboardIcon,
  ServicesIcon,
  SettingsIcon,
  TrendIcon,
  VisitorIcon,
} from './Icons';
import { AccountMenu } from './AccountMenu';
import { ThemeMenuButton } from './ThemeMenuButton';
import { useSessionState } from '@/context/SessionContext';

type NavItem = {
  href: string;
  label: string;
  icon: ReactNode;
  matchPrefixes?: string[];
};

const ADMIN_DESKTOP_NAV_ITEMS: NavItem[] = [
  { href: '/dashboard', label: 'Overview', icon: <DashboardIcon />, matchPrefixes: ['/explore'] },
  { href: '/analysis', label: 'Analysis', icon: <TrendIcon /> },
  { href: '/calendar', label: 'Calendar', icon: <CalendarIcon /> },
  { href: '/visitor', label: 'Visitor', icon: <VisitorIcon /> },
  { href: '/clients', label: 'Clients', icon: <ClientsIcon /> },
  { href: '/attendance', label: 'Attendance', icon: <AttendanceIcon /> },
  {
    href: '/content-management',
    label: 'Content',
    icon: <ServicesIcon />,
    matchPrefixes: ['/services', '/staff'],
  },
  { href: '/settings', label: 'Settings', icon: <SettingsIcon /> },
];

const ADMIN_MOBILE_NAV_ITEMS: NavItem[] = [
  { href: '/dashboard', label: 'Home', icon: <DashboardIcon />, matchPrefixes: ['/explore'] },
  { href: '/visitor', label: 'Visitor', icon: <VisitorIcon /> },
  { href: '/clients', label: 'Clients', icon: <ClientsIcon /> },
  { href: '/attendance', label: 'Attendance', icon: <AttendanceIcon /> },
  {
    href: '/content-management',
    label: 'Content',
    icon: <ServicesIcon />,
    matchPrefixes: ['/services', '/staff'],
  },
  { href: '/settings', label: 'More', icon: <SettingsIcon /> },
];

const STAFF_DESKTOP_NAV_ITEMS: NavItem[] = [
  { href: '/attendance', label: 'Attendance', icon: <AttendanceIcon /> },
  { href: '/visitor', label: 'Visitor', icon: <VisitorIcon /> },
  { href: '/clients', label: 'Clients', icon: <ClientsIcon /> },
  { href: '/settings', label: 'Settings', icon: <SettingsIcon /> },
];

const STAFF_MOBILE_NAV_ITEMS: NavItem[] = [
  { href: '/attendance', label: 'Shifts', icon: <AttendanceIcon /> },
  { href: '/visitor', label: 'Visitor', icon: <VisitorIcon /> },
  { href: '/clients', label: 'Clients', icon: <ClientsIcon /> },
  { href: '/settings', label: 'More', icon: <SettingsIcon /> },
];

function isNavItemActive(pathname: string, item: NavItem) {
  if (pathname === item.href) {
    return true;
  }

  if (item.href !== '/dashboard' && pathname.startsWith(item.href)) {
    return true;
  }

  return item.matchPrefixes?.some((prefix) => pathname.startsWith(prefix)) ?? false;
}

export function AppShell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { isStaff } = useSessionState();
  const desktopNavItems = isStaff ? STAFF_DESKTOP_NAV_ITEMS : ADMIN_DESKTOP_NAV_ITEMS;
  const mobileNavItems = isStaff ? STAFF_MOBILE_NAV_ITEMS : ADMIN_MOBILE_NAV_ITEMS;

  return (
    <div className="app-root app-shell-root">
      <div className="ambient-glow one" />
      <div className="ambient-glow two" />

      <div className="page-shell">
        <div className="page-shell-inner">
          <div className="page-surface">
            <header className="top-nav">
              <Link className="brand-lockup" to={isStaff ? '/attendance' : '/dashboard'} aria-label="SlotLooks home">
                <div className="brand-mark" aria-hidden="true">
                  <BrandMarkIcon />
                </div>
                <div className="brand-copy">
                  <div className="brand-name">SlotLooks</div>
                  <div className="brand-subtitle">Clients, shifts, bookings</div>
                </div>
              </Link>

              <nav className="nav-pills desktop-nav" aria-label="Primary">
                {desktopNavItems.map((item) => {
                  const active = isNavItemActive(location.pathname, item);

                  return (
                    <NavLink key={item.href} to={item.href} className={`nav-pill${active ? ' active' : ''}`}>
                      <span className="dot" aria-hidden="true" />
                      <span className="nav-icon" aria-hidden="true">
                        {item.icon}
                      </span>
                      <span className="label">{item.label}</span>
                    </NavLink>
                  );
                })}
              </nav>

              <div className="header-actions">
                <ThemeMenuButton />
                <AccountMenu />
              </div>
            </header>

            <main className="app-main">{children}</main>
          </div>
        </div>
      </div>

      <nav className="mobile-nav" aria-label="Mobile primary">
        {mobileNavItems.map((item) => {
          const active = isNavItemActive(location.pathname, item);

          return (
            <NavLink key={item.href} to={item.href} className={`nav-pill${active ? ' active' : ''}`}>
              <span className="nav-icon" aria-hidden="true">
                {item.icon}
              </span>
              <span className="label">{item.label}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
